import React, { useState } from "react";
import { Download, Eye, Settings, Check } from "lucide-react";

/**
 * Scoring Rules Presets Component
 * Allows admins to select from predefined scoring rule configurations
 */
export function ScoringRulesPresets({ onSelectPreset, currentRules = {}, className = "" }) {
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [showPreview, setShowPreview] = useState(null);

  const presets = [
    {
      id: 'standard_3_judges',
      name: 'Standard (Up to 3 Judges)',
      description: 'Traditional 3-judge scoring with no score dropping (minimum 3 judges)',
      icon: '📊',
      features: [
        'Up to 3 judges supported',
        'No score dropping',
        'Simple average calculation',
        'Standard competition format'
      ],
      rules: {
        minJudges: 3,
        maxJudges: 3,
        targetTotalScore: 300,
        maxPointsPerJudge: 100,
        dropLowestScore: false,
        dropScoreConditions: [],
        tieBreakingRules: [],
        missingJudgeRules: []
      }
    },
    {
      id: 'convention_3_4_judges',
      name: 'Convention (Up to 4 Judges)',
      description: 'Perfect for your convention setup with score dropping and tie-breaking (minimum 3 judges)',
      icon: '🏆',
      features: [
        'Up to 4 judges supported',
        'Drop lowest score when 4 judges present',
        'Add dropped score back for tie-breaking',
        'Average replacement for missing judges',
        'Target score: 300 points'
      ],
      rules: {
        minJudges: 3,
        maxJudges: 4,
        targetTotalScore: 300,
        maxPointsPerJudge: 100,
        dropLowestScore: true,
        dropScoreConditions: [
          {
            id: "four_judges",
            condition: "judgeCount >= 4",
            action: "dropLowest",
            count: 1,
            description: "When 4 judges are present, drop the lowest score"
          }
        ],
        tieBreakingRules: [
          {
            id: "add_dropped_score",
            condition: "tie && droppedScoresExist",
            action: "addDroppedScores",
            description: "In case of tie with 4 judges, add back the dropped score"
          }
        ],
        missingJudgeRules: [
          {
            id: "average_replacement",
            condition: "judgeCount < minJudges",
            action: "averageReplacement",
            description: "If a judge misses, use average of other judges' scores"
          }
        ]
      }
    },
    {
      id: 'standard_5_judges_drop_2',
      name: 'Olympic Style (5 Judges)',
      description: '5-judge scoring with highest and lowest scores dropped',
      icon: '🥇',
      features: [
        'Exactly 5 judges required',
        'Drop highest and lowest scores',
        'Use middle 3 scores for final',
        'Olympic-style judging'
      ],
      rules: {
        minJudges: 5,
        maxJudges: 5,
        targetTotalScore: 300,
        maxPointsPerJudge: 100,
        dropLowestScore: true,
        dropScoreConditions: [
          {
            id: "drop_lowest",
            condition: "judgeCount >= 5",
            action: "dropLowest",
            count: 1,
            description: "Drop lowest score"
          },
          {
            id: "drop_highest",
            condition: "judgeCount >= 5",
            action: "dropHighest",
            count: 1,
            description: "Drop highest score"
          }
        ],
        tieBreakingRules: [],
        missingJudgeRules: []
      }
    }
  ];

  const handleSelectPreset = (preset) => {
    setSelectedPreset(preset.id);
    onSelectPreset(preset.rules);
  };

  const isCurrentPreset = (preset) => {
    if (!currentRules || Object.keys(currentRules).length === 0) return false;
    
    // Simple comparison - in production, you'd want a more sophisticated comparison
    return (
      currentRules.minJudges === preset.rules.minJudges &&
      currentRules.maxJudges === preset.rules.maxJudges &&
      currentRules.targetTotalScore === preset.rules.targetTotalScore &&
      currentRules.dropLowestScore === preset.rules.dropLowestScore
    );
  };

  const PresetCard = ({ preset }) => {
    const isCurrent = isCurrentPreset(preset);
    const isSelected = selectedPreset === preset.id;
    
    return (
      <div className={`card bg-base-100 border-2 transition-all duration-200 ${
        isCurrent ? 'border-success bg-success/5' : 
        isSelected ? 'border-primary' : 'border-base-300 hover:border-base-400'
      }`}>
        <div className="card-body p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="text-2xl">{preset.icon}</div>
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  {preset.name}
                  {isCurrent && <Check className="w-5 h-5 text-success" />}
                </h3>
                <p className="text-sm text-base-content/70">{preset.description}</p>
              </div>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            {preset.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                <span>{feature}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => handleSelectPreset(preset)}
              disabled={isCurrent}
              className={`btn btn-sm flex-1 ${
                isCurrent ? 'btn-success' : 'btn-primary'
              }`}
            >
              <Download className="w-4 h-4 mr-2" />
              {isCurrent ? 'Current' : 'Use This'}
            </button>
            <button
              onClick={() => setShowPreview(showPreview === preset.id ? null : preset.id)}
              className="btn btn-sm btn-outline"
            >
              <Eye className="w-4 h-4" />
            </button>
          </div>

          {showPreview === preset.id && (
            <div className="mt-4 p-4 bg-base-200 rounded-lg">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Configuration Preview
              </h4>
              <div className="text-xs space-y-1">
                <div><strong>Judges:</strong> {
                  preset.rules.minJudges === preset.rules.maxJudges 
                    ? preset.rules.maxJudges 
                    : `${preset.rules.minJudges}-${preset.rules.maxJudges}`
                }</div>
                <div><strong>Target Score:</strong> {preset.rules.targetTotalScore}</div>
                <div><strong>Max per Judge:</strong> {preset.rules.maxPointsPerJudge}</div>
                <div><strong>Score Dropping:</strong> {preset.rules.dropLowestScore ? 'Yes' : 'No'}</div>
                
                {/* Detailed drop rules explanation */}
                {preset.id === 'convention_3_4_judges' && (
                  <div className="mt-2 p-2 bg-base-300 rounded text-xs">
                    <div><strong>Drop Rules:</strong></div>
                    <div>• With 4 judges: Drop the lowest score</div>
                    <div>• With 3 judges: Use all scores</div>
                    <div>• Tie-breaking: Add dropped score back if tied</div>
                    <div>• Missing judge: Use average of other judges</div>
                  </div>
                )}
                
                {preset.id === 'standard_5_judges_drop_2' && (
                  <div className="mt-2 p-2 bg-base-300 rounded text-xs">
                    <div><strong>Drop Rules:</strong></div>
                    <div>• Always drop highest AND lowest scores</div>
                    <div>• Use middle 3 scores for final calculation</div>
                    <div>• Requires exactly 5 judges to function</div>
                    <div>• True Olympic-style judging system</div>
                  </div>
                )}
                
                {preset.id === 'standard_3_judges' && (
                  <div className="mt-2 p-2 bg-base-300 rounded text-xs">
                    <div><strong>Scoring Method:</strong></div>
                    <div>• Simple average of all 3 judges</div>
                    <div>• No scores dropped or modified</div>
                    <div>• Straightforward calculation</div>
                    <div>• Most common competition format</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Choose a Scoring System</h2>
        <p className="text-base-content/70">
          Select a preset that matches your competition format, or customize your own rules
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {presets.map((preset) => (
          <PresetCard key={preset.id} preset={preset} />
        ))}
      </div>

      <div className="alert alert-info">
        <div className="flex items-start gap-3">
          <Settings className="w-5 h-5 mt-0.5" />
          <div>
            <div className="font-semibold">Need something different?</div>
            <div className="text-sm">
              After selecting a preset, you can customize all the rules in the "Scoring Rules" tab
              to match your specific competition requirements.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
